import { Error } from "@mui/icons-material";
import { Box, Stack, useTheme } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";
import { answerColorToThemeColor } from "components/Evaluation/Utils";
import { observer } from "mobx-react";
import { TestLLMClassifierAgreementResponse } from "models/LLMs/Testing/TestLLMClassifierAgreementResponse";
import React from "react";
import { useStore } from "utils/useStore";
import { getFriendlyId } from "../utils";
import { SuccessfulResultsSection } from "./SuccessfulResultsSection";
import { ErrorResultsSection } from "./ErrorResultsSection";
import { ScrollToResultButton } from "../ScrollToResultButton";
import { ExportToCSV } from "components/CSVExport/ExportToCSV";
import {
    filterBySource,
    hasBothSources,
    ResultSource,
} from "./ResultSourceFilter";
import { SourceAgreementCard } from "./SourceAgreementCard";

interface Props {
    data: TestLLMClassifierAgreementResponse;
}

export const AgreementTestingResults = observer(({ data }: Props) => {
    const theme = useTheme();
    const store = useStore(LLMClassifierBuilderStore);

    const labels = store.classifier.outputs.map((output) => output.result);
    const sections = labels
        .map((label) => ({
            label,
            results: data.labelToResults[label] ?? [],
            colors: answerColorToThemeColor(theme, store.getResultColor(label)),
        }))
        .concat({
            label: "No Agreement",
            results: data.noAgreementResults
                .slice()
                .sort((a, b) =>
                    a.conversation.labeled.localeCompare(
                        b.conversation.labeled,
                    ),
                ),
            colors: answerColorToThemeColor(theme),
        })
        .sort((a, b) => b.results.length - a.results.length);

    const agreedResults = Object.values(data.labelToResults).flat();
    const allResults = agreedResults.concat(
        data.noAgreementResults,
        data.errorResults,
    );
    // Human and synthetic agreement are shown apart once a test mixes both.
    const showBySource = hasBothSources(allResults);
    const getSourceResults = (source: ResultSource) => ({
        agreed: filterBySource(agreedResults, source),
        noAgreement: filterBySource(data.noAgreementResults, source),
        errors: filterBySource(data.errorResults, source),
    });
    const humanCount = filterBySource(allResults, "human").length;
    const syntheticCount = filterBySource(allResults, "synthetic").length;
    // One card per source the test used.
    const sourceCards: {
        variant: Exclude<ResultSource, "all">;
        title: string;
        description: string;
        count: number;
    }[] = [
        {
            variant: "human",
            title: "Human labels",
            description: `${humanCount} calls labeled in ${
                store.dataLabelingModule?.name ?? "the selected module"
            }`,
            count: humanCount,
        },
        {
            variant: "synthetic",
            title: "Synthetic",
            description: `${syntheticCount} synthetic calls`,
            count: syntheticCount,
        },
    ];

    const errorsSection = {
        label: "Errors",
        results: data.errorResults,
        colors: {
            color: theme.palette.red.main,
            borderColor: theme.palette.red.main,
            backgroundColor: theme.palette.red.background,
        },
    };
    const buttonSx = (colors: {
        borderColor?: string;
        backgroundColor?: string;
    }) => ({
        border: `1px solid ${colors.borderColor}`,
        backgroundColor: colors.backgroundColor,
    });

    return (
        <TitledSection
            title={`Results (${data.processedCount})`}
            subtitle={
                showBySource && (
                    <Box
                        component="span"
                        sx={(theme) => ({ color: theme.palette.gray[500] })}
                    >
                        {humanCount} human labels + {syntheticCount} synthetic
                        calls
                    </Box>
                )
            }
            action={
                <ExportToCSV
                    data={allResults}
                    selectedKeys={store.selectedExportKeys}
                    onSelectedKeysChange={(keys) =>
                        store.setSelectedExportKeys(keys)
                    }
                    getLabelForKey={(key) => {
                        switch (key) {
                            case "conversation.labeled":
                                return "Human Label";
                            case "decision":
                                return "Model Label";
                            case "reasoning":
                                return "Model Reasoning";
                        }
                    }}
                />
            }
            variant="bordered"
            sx={{ width: "100%" }}
        >
            <Stack rowGap={1} padding={2} width="100%">
                <TitledSection title="Agreement" variant="bordered">
                    <Stack direction="row" columnGap={3} width="100%">
                        {sourceCards
                            .filter((card) => card.count > 0)
                            .map((card) => (
                                <SourceAgreementCard
                                    key={card.variant}
                                    variant={card.variant}
                                    title={card.title}
                                    description={card.description}
                                    labels={labels}
                                    {...getSourceResults(card.variant)}
                                />
                            ))}
                    </Stack>
                    <Stack
                        direction="row"
                        columnGap={2}
                        marginTop={2}
                        width="100%"
                    >
                        {errorsSection.results.length > 0 && (
                            <ScrollToResultButton
                                title={
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        columnGap={1}
                                        color={errorsSection.colors.color}
                                    >
                                        <Error color="error" />
                                        {errorsSection.label}
                                    </Stack>
                                }
                                count={errorsSection.results.length}
                                scrollToId={getFriendlyId(errorsSection.label)}
                                sx={buttonSx(errorsSection.colors)}
                            />
                        )}
                        {sections.map((section) => (
                            <ScrollToResultButton
                                key={section.label}
                                title={section.label}
                                count={section.results.length}
                                scrollToId={getFriendlyId(section.label)}
                                sx={buttonSx(section.colors)}
                            />
                        ))}
                    </Stack>
                </TitledSection>
                <ErrorResultsSection results={errorsSection.results} />
                {sections.map((section) => (
                    <SuccessfulResultsSection
                        key={section.label}
                        results={section.results}
                        label={section.label}
                    />
                ))}
            </Stack>
        </TitledSection>
    );
});
